/**
 * EAFileList Component
 * Displays list of MQL5 files from user's MT5 installation
 *
 * Features:
 * - File type badges (EA/IND/SCR) with color coding
 * - Type filter dropdown
 * - Search by name
 * - Refresh button
 * - Selection highlighting
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Tooltip,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import CodeIcon from '@mui/icons-material/Code';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { EA_FILE_TYPES, getFileTypeConfig } from '../../../api/qbridgeEaApi';

/**
 * File Type Badge Component
 * Displays colored badge for EA/IND/SCR
 */
const FileTypeBadge = ({ fileType }) => {
  const config = getFileTypeConfig(fileType);

  return (
    <Chip
      label={config.label}
      size="small"
      sx={{
        backgroundColor: `${config.color}20`,
        color: config.color,
        fontWeight: 700,
        fontSize: '0.6875rem',
        height: '22px',
        minWidth: '36px',
        borderRadius: '4px',
        border: `1px solid ${config.color}40`,
        '& .MuiChip-label': {
          px: 0.75
        }
      }}
    />
  );
};

/**
 * EAFileList Component
 * @param {Array} files - List of EA files from QBridge
 * @param {object} summary - File count summary
 * @param {boolean} isLoading - Loading state
 * @param {string} error - Error message
 * @param {object} selectedFile - Currently selected file
 * @param {function} onSelectFile - File selection handler
 * @param {function} onRefresh - Refresh handler
 * @param {boolean} qbridgeOnline - QBridge connection status
 * @param {Array} analyzedFiles - List of file names that have been analyzed before
 */
const EAFileList = ({
  files = [],
  summary = {},
  isLoading = false,
  error = null,
  selectedFile = null,
  onSelectFile,
  onRefresh,
  qbridgeOnline = false,
  analyzedFiles = []
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  // Filter files based on search and type
  const filteredFiles = useMemo(() => {
    return files.filter(file => {
      // Type filter
      if (typeFilter !== 'all' && file.file_type !== typeFilter) {
        return false;
      }

      // Search filter (case-insensitive)
      if (searchQuery) {
        return file.name.toLowerCase().includes(searchQuery.toLowerCase());
      }

      return true;
    });
  }, [files, searchQuery, typeFilter]);

  // Calculate counts for each type
  // Support both new (experts) and legacy (experts_count) field names
  const typeCounts = useMemo(() => ({
    all: files.length,
    expert: summary.experts ?? summary.experts_count ?? files.filter(f => f.file_type === 'expert').length,
    indicator: summary.indicators ?? summary.indicators_count ?? files.filter(f => f.file_type === 'indicator').length,
    script: summary.scripts ?? summary.scripts_count ?? files.filter(f => f.file_type === 'script').length
  }), [files, summary]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: '#0F0F14',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        overflow: 'hidden'
      }}
    >
      {/* ════════════════════════════════════════════════════════════════════════
          HEADER - Search & Filters
          ════════════════════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          padding: '16px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          backgroundColor: 'rgba(255, 255, 255, 0.02)'
        }}
      >
        {/* Title Row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CodeIcon sx={{ color: '#7c3aed', fontSize: '1.25rem' }} />
            <Typography
              sx={{
                color: '#FFFFFF',
                fontSize: '1rem',
                fontWeight: 600,
                fontFamily: "'Inter', -apple-system, sans-serif"
              }}
            >
              My EAs
            </Typography>
            <Chip
              label={typeCounts.all}
              size="small"
              sx={{
                backgroundColor: 'rgba(124, 58, 237, 0.15)',
                color: '#a855f7',
                fontSize: '0.75rem',
                fontWeight: 600,
                height: '20px'
              }}
            />
          </Box>

          <Tooltip title="Refresh file list">
            {/* Wrap in span to allow Tooltip to work with disabled button */}
            <span>
              <IconButton
                onClick={onRefresh}
                disabled={isLoading || !qbridgeOnline}
                size="small"
                sx={{
                  color: '#71717A',
                  '&:hover': {
                    color: '#27B56D',
                    backgroundColor: 'rgba(39, 181, 109, 0.1)'
                  },
                  '&.Mui-disabled': {
                    color: 'rgba(255, 255, 255, 0.2)'
                  }
                }}
              >
                <RefreshIcon
                  sx={{
                    fontSize: '1.25rem',
                    animation: isLoading ? 'spin 1s linear infinite' : 'none',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' }
                    }
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {/* Search Input */}
        <TextField
          fullWidth
          placeholder="Search files..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#71717A', fontSize: '1.125rem' }} />
              </InputAdornment>
            )
          }}
          sx={{
            mb: 1.5,
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
              borderRadius: '8px',
              '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.1)'
              },
              '&:hover fieldset': {
                borderColor: 'rgba(124, 58, 237, 0.3)'
              },
              '&.Mui-focused fieldset': {
                borderColor: '#7c3aed'
              }
            },
            '& .MuiInputBase-input': {
              color: '#FFFFFF',
              fontSize: '0.875rem',
              '&::placeholder': {
                color: '#71717A',
                opacity: 1
              }
            }
          }}
        />

        {/* Type Filter */}
        <FormControl fullWidth size="small">
          <Select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
              borderRadius: '8px',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.1)'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(124, 58, 237, 0.3)'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#7c3aed'
              },
              '& .MuiSelect-select': {
                color: '#FFFFFF',
                fontSize: '0.875rem'
              },
              '& .MuiSelect-icon': {
                color: '#71717A'
              }
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: '#1A1A1F',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  '& .MuiMenuItem-root': {
                    color: '#FFFFFF',
                    fontSize: '0.875rem',
                    '&:hover': {
                      backgroundColor: 'rgba(124, 58, 237, 0.1)'
                    },
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(124, 58, 237, 0.2)',
                      '&:hover': {
                        backgroundColor: 'rgba(124, 58, 237, 0.25)'
                      }
                    }
                  }
                }
              }
            }}
          >
            <MenuItem value="all">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                All Types
                <Chip
                  label={typeCounts.all}
                  size="small"
                  sx={{
                    height: '18px',
                    fontSize: '0.6875rem',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'rgba(255, 255, 255, 0.7)'
                  }}
                />
              </Box>
            </MenuItem>
            <MenuItem value="expert">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FileTypeBadge fileType="expert" />
                Expert Advisors
                <Chip
                  label={typeCounts.expert}
                  size="small"
                  sx={{
                    height: '18px',
                    fontSize: '0.6875rem',
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    color: '#3B82F6'
                  }}
                />
              </Box>
            </MenuItem>
            <MenuItem value="indicator">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FileTypeBadge fileType="indicator" />
                Indicators
                <Chip
                  label={typeCounts.indicator}
                  size="small"
                  sx={{
                    height: '18px',
                    fontSize: '0.6875rem',
                    backgroundColor: 'rgba(34, 197, 94, 0.1)',
                    color: '#22C55E'
                  }}
                />
              </Box>
            </MenuItem>
            <MenuItem value="script">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FileTypeBadge fileType="script" />
                Scripts
                <Chip
                  label={typeCounts.script}
                  size="small"
                  sx={{
                    height: '18px',
                    fontSize: '0.6875rem',
                    backgroundColor: 'rgba(249, 115, 22, 0.1)',
                    color: '#F97316'
                  }}
                />
              </Box>
            </MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* ════════════════════════════════════════════════════════════════════════
          FILE LIST
          ════════════════════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            width: '6px'
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '3px',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)'
            }
          }
        }}
      >
        {/* Loading State */}
        {isLoading && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 4,
              gap: 2
            }}
          >
            <CircularProgress size={32} sx={{ color: '#7c3aed' }} />
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.875rem' }}>
              Scanning MQL5 files...
            </Typography>
          </Box>
        )}

        {/* Error State */}
        {!isLoading && error && (
          <Box sx={{ p: 2 }}>
            <Alert
              severity="error"
              sx={{
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                color: '#EF4444',
                border: '1px solid rgba(239, 68, 68, 0.2)',
                '& .MuiAlert-icon': {
                  color: '#EF4444'
                }
              }}
            >
              {error}
            </Alert>
          </Box>
        )}

        {/* QBridge Offline State */}
        {!isLoading && !error && !qbridgeOnline && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 4,
              px: 3,
              textAlign: 'center'
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: 'rgba(251, 191, 36, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2
              }}
            >
              <CodeIcon sx={{ color: '#FBBF24', fontSize: '1.5rem' }} />
            </Box>
            <Typography
              sx={{
                color: '#FFFFFF',
                fontSize: '0.9375rem',
                fontWeight: 600,
                mb: 0.5
              }}
            >
              QBridge Not Connected
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255, 255, 255, 0.5)',
                fontSize: '0.8125rem',
                maxWidth: '240px'
              }}
            >
              Start QBridge to view and analyze your MT5 Expert Advisors
            </Typography>
          </Box>
        )}

        {/* Empty State */}
        {!isLoading && !error && qbridgeOnline && filteredFiles.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 4,
              px: 3,
              textAlign: 'center'
            }}
          >
            <Typography
              sx={{
                color: 'rgba(255, 255, 255, 0.5)',
                fontSize: '0.875rem'
              }}
            >
              {searchQuery
                ? `No files matching "${searchQuery}"`
                : 'No MQL5 files found'}
            </Typography>
          </Box>
        )}

        {/* File List */}
        {!isLoading && !error && qbridgeOnline && filteredFiles.length > 0 && (
          <List sx={{ py: 1, px: 0.5 }}>
            {filteredFiles.map((file, index) => {
              // Use combination of file_type + name as unique key
              // This handles duplicates like FFT.mq5 in both Experts and Scripts folders
              const uniqueKey = `${file.file_type}_${file.name}_${index}`;
              const isSelected = selectedFile?.name === file.name && selectedFile?.file_type === file.file_type;
              const wasAnalyzed = analyzedFiles.includes(file.name);

              return (
                <ListItem key={uniqueKey} disablePadding sx={{ px: 0.75, py: 0.375 }}>
                  <ListItemButton
                    onClick={() => onSelectFile(file)}
                    selected={isSelected}
                    sx={{
                      borderRadius: '10px',
                      py: 1.5,
                      px: 1.75,
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(124, 58, 237, 0.08)'
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(124, 58, 237, 0.15)',
                        border: '1px solid rgba(124, 58, 237, 0.3)',
                        '&:hover': {
                          backgroundColor: 'rgba(124, 58, 237, 0.2)'
                        }
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, width: '100%' }}>
                      <FileTypeBadge fileType={file.file_type} />
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                              sx={{
                                color: '#FFFFFF',
                                fontSize: '0.875rem',
                                fontWeight: isSelected ? 600 : 500,
                                fontFamily: "'JetBrains Mono', monospace",
                                wordBreak: 'break-all'
                              }}
                            >
                              {file.name}
                            </Typography>
                            {/* History Badge - Shows if file was previously analyzed */}
                            {wasAnalyzed && (
                              <Tooltip title="Previously analyzed by Q.AI">
                                <AutoAwesomeIcon
                                  sx={{
                                    fontSize: '0.875rem',
                                    color: '#a855f7'
                                  }}
                                />
                              </Tooltip>
                            )}
                            {file.has_qai_version && (
                              <Tooltip title="Has Q.AI version">
                                <CheckCircleIcon
                                  sx={{
                                    fontSize: '0.875rem',
                                    color: '#27B56D'
                                  }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        }
                        secondary={
                          <Typography
                            sx={{
                              color: 'rgba(255, 255, 255, 0.4)',
                              fontSize: '0.75rem',
                              mt: 0.25
                            }}
                          >
                            {file.line_count?.toLocaleString() || '?'} lines
                            {(typeof file.size_kb === 'number') && ` • ${file.size_kb.toFixed(1)} KB`}
                          </Typography>
                        }
                        sx={{ margin: 0 }}
                      />
                    </Box>
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>

      {/* ════════════════════════════════════════════════════════════════════════
          FOOTER - Summary
          ════════════════════════════════════════════════════════════════════════ */}
      {qbridgeOnline && !isLoading && filteredFiles.length > 0 && (
        <Box
          sx={{
            padding: '12px 16px',
            borderTop: '1px solid rgba(255, 255, 255, 0.08)',
            backgroundColor: 'rgba(255, 255, 255, 0.02)'
          }}
        >
          <Typography
            sx={{
              color: 'rgba(255, 255, 255, 0.4)',
              fontSize: '0.75rem',
              textAlign: 'center'
            }}
          >
            {filteredFiles.length === files.length
              ? `${files.length} files`
              : `${filteredFiles.length} of ${files.length} files`}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default EAFileList;
